import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { 
  Package,
  Search, 
  Palette, 
  Trophy, 
  ShoppingBag,
  CheckCircle,
  Clock,
  TrendingUp
} from 'lucide-react';

const stageConfig = {
  sniped: {
    icon: Package,
    color: 'border-t-slate-400',
    bgColor: 'bg-slate-50',
    accentColor: 'text-slate-600',
    route: null // No navigation for sniped
  },
  researching: {
    icon: Search,
    color: 'border-t-cyan-500',
    bgColor: 'bg-cyan-50',
    accentColor: 'text-cyan-600',
    route: '/product-research'
  },
  creative: {
    icon: Palette,
    color: 'border-t-violet-500',
    bgColor: 'bg-violet-50',
    accentColor: 'text-violet-600',
    route: '/ads'
  },
  winning: {
    icon: Trophy,
    color: 'border-t-emerald-500',
    bgColor: 'bg-emerald-50',
    accentColor: 'text-emerald-600',
    route: '/winning-products'
  },
  shopify: {
    icon: ShoppingBag,
    color: 'border-t-indigo-500',
    bgColor: 'bg-indigo-50',
    accentColor: 'text-indigo-600',
    route: '/shopify'
  }
};

export function StageCard({ 
  stage, 
  metrics = { total: 0, completed: 0, inProgress: 0, completionRate: 0 },
  isActive = false,
  onClick 
}) {
  const navigate = useNavigate();
  const config = stageConfig[stage] || stageConfig.sniped;
  const Icon = config.icon;

  const handleClick = () => {
    if (onClick) onClick();
    if (config.route) {
      navigate(config.route);
    }
  };

  return (
    <div
      data-testid={`stage-card-${stage}`}
      onClick={handleClick}
      className={cn(
        'bg-white border-2 border-slate-200 rounded-xl p-3 md:p-5 transition-all duration-300',
        config.route ? 'cursor-pointer hover:shadow-lg' : 'cursor-default',
        config.color,
        'border-t-4',
        isActive && 'ring-2 ring-offset-2 ring-cyan-400 shadow-lg'
      )}
    >
      {/* Header - Stacked on mobile, inline on desktop */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2 mb-1 md:mb-0">
          <div className={cn('p-1.5 md:p-2 rounded-lg', config.bgColor)}>
            <Icon className={cn('w-3 h-3 md:w-4 md:h-4', config.accentColor)} />
          </div>
          <h3 className="text-[10px] md:text-xs font-bold text-slate-600 uppercase tracking-wide">
            {stage}
          </h3>
        </div>
        <span className="text-xl md:text-2xl font-bold text-[#1E1B4B]">{metrics.total}</span>
      </div>

      {/* Stats Grid - Stacked on mobile */}
      <div className="flex flex-col md:grid md:grid-cols-2 gap-1.5 md:gap-2 mb-2 md:mb-3">
        <div className={cn('p-1.5 md:p-2 rounded-lg flex items-center justify-between md:block', config.bgColor)}>
          <div className="flex items-center gap-1 md:mb-0.5">
            <CheckCircle className="w-2.5 h-2.5 md:w-3 md:h-3 text-emerald-500" />
            <span className="text-[9px] md:text-[10px] font-medium text-slate-500">Complete</span>
          </div>
          <span className="text-sm md:text-lg font-bold text-emerald-600">{metrics.completed}</span>
        </div>
        <div className="p-1.5 md:p-2 rounded-lg bg-amber-50 flex items-center justify-between md:block">
          <div className="flex items-center gap-1 md:mb-0.5">
            <Clock className="w-2.5 h-2.5 md:w-3 md:h-3 text-amber-500" />
            <span className="text-[9px] md:text-[10px] font-medium text-slate-500">Progress</span>
          </div>
          <span className="text-sm md:text-lg font-bold text-amber-600">{metrics.inProgress}</span>
        </div>
      </div>

      {/* Completion Rate */}
      <div className="flex items-center gap-1.5 md:gap-2">
        <TrendingUp className="w-2.5 h-2.5 md:w-3 md:h-3 text-slate-500 flex-shrink-0" />
        <div className="flex-1 bg-slate-200 rounded-full h-1 md:h-1.5 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              metrics.completionRate >= 80 ? 'bg-emerald-500' :
              metrics.completionRate >= 50 ? 'bg-cyan-500' :
              metrics.completionRate >= 20 ? 'bg-amber-500' : 'bg-slate-400'
            )}
            style={{ width: `${metrics.completionRate}%` }}
          />
        </div>
        <span className="text-[10px] md:text-xs font-semibold text-slate-600 min-w-[2rem] md:min-w-[2.5rem] text-right">
          {metrics.completionRate}%
        </span>
      </div>

      {/* Navigation hint - Hidden on mobile to save space */}
      {config.route && (
        <p className="hidden md:block text-[10px] text-slate-400 mt-2 text-center">
          Click to view details →
        </p>
      )}
    </div>
  );
}
